import { useMemo } from "react";

/**
 * Combines actual and requested goods into a single array to be used in the react-table
 * @param actualGoods - This is the array of actual goods from the draft object
 * @param currentInvoice - This is the currently selected invoice object from the shipment
 * @returns a combined array of actual and requested goods
 */
const useTableData = (actualGoods: any, currentInvoice: any) =>
  useMemo(() => {
    const combinedItems: any = [];

    // Create a map of requested goods by their id for easy lookup
    const requestedGoodsMap = currentInvoice?.good_items?.requested_good_items.reduce((map: any, item: any) => {
      map[item.id] = item;
      return map;
    }, {});

    // Map actual goods and match them with corresponding requested goods if available
    actualGoods?.forEach((actualItem: any, index: number) => {
      const idToMatch = actualItem.parent_id || actualItem.id;
      const requestedItem = requestedGoodsMap[idToMatch];
      const combinedItem: any = {};

      // Prefix actual data keys with 'actual_'
      Object.keys(actualItem).forEach((key) => {
        combinedItem[`actual_${key}`] = actualItem[key];
      });

      // If there's a corresponding requested item, prefix its keys with 'requested_'
      if (requestedItem) {
        Object.keys(requestedItem).forEach((key) => {
          combinedItem[`requested_${key}`] = requestedItem[key];
        });
        // Remove the matched requested item from the map to avoid duplicate inclusion
        delete requestedGoodsMap[idToMatch];
      }

      // Add the initial index from actualGoods
      combinedItem.index = index;

      combinedItems.push(combinedItem);
    });

    // Add remaining requested items that don't have corresponding actual goods
    Object.values(requestedGoodsMap).forEach((requestedItem: any) => {
      const combinedItem: any = {};
      // Prefix requested data keys with 'requested_'
      Object.keys(requestedItem).forEach((key) => {
        combinedItem[`requested_${key}`] = requestedItem[key];
      });
      combinedItems.push(combinedItem);
    });

    // Sort combinedItems array by id, if there is no id the item will be placed at the end
    combinedItems.sort((a: any, b: any) => {
      const idA = a.actual_id || a.requested_id || Infinity; // Use Infinity if id is missing
      const idB = b.actual_id || b.requested_id || Infinity; // Use Infinity if id is missing
      return idA - idB;
    });
    return combinedItems;
  }, [actualGoods, currentInvoice]);

export default useTableData;
